import {
  MARKETPLACE_CURRENCY,
  TEMPLATE_NAME_PREFIX_MAX_LENGTH,
  type TemplatePortfolioDraft,
} from "@adpilot/shared";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  IconAlertCircle,
  IconArrowRight,
  IconBuildingStore,
  IconPlayerPause,
} from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { format } from "date-fns";
import { useEffect, useMemo, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { z } from "zod";
import { ProductListingPicker } from "@/components/campaign-creation/ProductListingPicker";
import { filterSwitcherConnections, marketplaceCode } from "@/components/layout/shop-scope-select";
import {
  AlertDialog,
  AlertDialogClose,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogPopup,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Field, FieldError, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { type AccountShop, fetchAmazonAccounts } from "@/lib/amazon-api";
import { getApiErrorMessage } from "@/lib/api-error";
import type { SelectedProduct } from "@/lib/campaign-creation-state";
import { fetchPortfolios } from "@/lib/portfolio-api";
import { cn } from "@/lib/utils";
import { useAuthStore } from "@/stores/auth";
import {
  type TemplatePortfolioEditableSettings,
  useCreateTemplatePortfolioDraft,
  useUpdateTemplatePortfolioDraft,
} from "./api";
import { TemplatePortfolioFooter } from "./TemplatePortfolioShell";

const NAME_MAX = 128;
const NO_INVALID_PRODUCTS: never[] = [];
const noop = () => {};

export function isUsdShop(shop: Pick<AccountShop, "marketplaceId">): boolean {
  return MARKETPLACE_CURRENCY[shop.marketplaceId] === "USD";
}

/** Default 组合名称 (docs/tech/12 §3 ①): 【{前缀}{ASIN}】; empty until both are chosen. */
export function defaultPortfolioName(namePrefix: string, asin: string | undefined): string {
  const prefix = namePrefix.trim();
  return prefix && asin ? `【${prefix}${asin}】` : "";
}

/** 「美国」/「日本」 next to the shop name (Paper TP-01c). */
function marketplaceCountry(marketplaceName: string, locale: string): string {
  const code = marketplaceCode(marketplaceName);
  try {
    return (
      new Intl.DisplayNames([locale], { type: "region" }).of(code === "UK" ? "GB" : code) ?? code
    );
  } catch {
    return code;
  }
}

/** A vendor's listing key is its ASIN and it has no SKU; a seller's key is the SKU (#673). */
function productFromDraft(
  draft: TemplatePortfolioDraft | undefined,
  vendor: boolean,
): SelectedProduct | null {
  const product = draft?.settings.product;
  if (!product) return null;
  return {
    asin: product.asin,
    listingKey: product.listingKey,
    sku: vendor ? null : product.listingKey,
    variationGroupKey: product.asin,
    title: null,
    imageUrl: null,
  };
}

export interface StepBasicsProps {
  /** Shop chosen in the form (URL `shopId`); null until the workspace has one. */
  shopId: string | null;
  /** Existing draft when resuming or coming back from ②. */
  draft?: TemplatePortfolioDraft;
  /** Any existing draft belongs to the previous shop; the page drops it and ① saves a new one. */
  onShopChange: (shopId: string) => void;
  onSaved: (draft: TemplatePortfolioDraft) => void;
}

export function StepBasics({ shopId, draft, onShopChange, onSaved }: StepBasicsProps) {
  const { t, i18n } = useTranslation("templatePortfolio");
  const navigate = useNavigate();
  const activeWorkspaceId = useAuthStore((state) => state.user?.activeWorkspaceId ?? null);
  const accounts = useQuery({
    queryKey: ["amazon", "accounts"],
    queryFn: fetchAmazonAccounts,
    staleTime: 60_000,
  });
  const shops = useMemo(
    () =>
      filterSwitcherConnections(accounts.data?.connections ?? [], activeWorkspaceId).flatMap(
        (connection) => connection.shops,
      ),
    [accounts.data, activeWorkspaceId],
  );
  const shop = shops.find((candidate) => candidate.shopId === shopId);
  const shopError = !shop
    ? accounts.isSuccess
      ? t("basics.shopRequired")
      : null
    : isUsdShop(shop)
      ? null
      : t("basics.shopUnsupported");
  const [pendingShopId, setPendingShopId] = useState<string | null>(null);

  const schema = useMemo(
    () =>
      z.object({
        product: z
          .custom<SelectedProduct | null>()
          .refine((product) => product != null, t("basics.productRequired")),
        namePrefix: z
          .string()
          .trim()
          .min(1, t("basics.namePrefixRequired"))
          .max(
            TEMPLATE_NAME_PREFIX_MAX_LENGTH,
            t("basics.tooLong", { max: TEMPLATE_NAME_PREFIX_MAX_LENGTH }),
          ),
        portfolioName: z
          .string()
          .trim()
          .min(1, t("basics.portfolioNameRequired"))
          .max(NAME_MAX, t("basics.tooLong", { max: NAME_MAX })),
      }),
    [t],
  );

  const vendor = shop?.accountType === "vendor";
  const initialProduct = productFromDraft(draft, vendor);
  const initialPrefix = draft?.settings.namePrefix ?? "";
  const form = useForm<z.input<typeof schema>, unknown, z.output<typeof schema>>({
    resolver: zodResolver(schema),
    defaultValues: {
      product: initialProduct,
      namePrefix: initialPrefix,
      portfolioName:
        draft?.settings.portfolioName ?? defaultPortfolioName(initialPrefix, initialProduct?.asin),
    },
  });
  // The name follows 前缀 + ASIN until the user types their own.
  const nameEdited = useRef(
    draft?.settings.portfolioName != null &&
      draft.settings.portfolioName !== defaultPortfolioName(initialPrefix, initialProduct?.asin),
  );
  // The prefix defaults to the shop name until the user types their own.
  const prefixEdited = useRef(Boolean(draft?.settings.namePrefix));
  const shopName = shop?.shopName;
  useEffect(() => {
    if (prefixEdited.current || !shopName) return;
    form.setValue("namePrefix", shopName);
    if (!nameEdited.current) {
      form.setValue(
        "portfolioName",
        defaultPortfolioName(shopName, form.getValues("product")?.asin),
      );
    }
  }, [form, shopName]);
  function syncPortfolioName(next: { namePrefix?: string; product?: SelectedProduct | null }) {
    if (nameEdited.current) return;
    const namePrefix = next.namePrefix ?? form.getValues("namePrefix");
    const product = next.product === undefined ? form.getValues("product") : next.product;
    form.setValue("portfolioName", defaultPortfolioName(namePrefix, product?.asin));
  }
  const product = form.watch("product");
  const namePrefix = form.watch("namePrefix");
  const portfolioName = form.watch("portfolioName");
  const defaultName = defaultPortfolioName(namePrefix, product?.asin);
  // Stable array: the picker re-syncs its selection state whenever this identity changes.
  const selectedProducts = useMemo(() => (product ? [product] : []), [product]);

  // Amazon rejects duplicate portfolio names (archived included); never auto-suffix.
  const portfolios = useQuery({
    queryKey: ["portfolios", "template-portfolio", shopId],
    queryFn: () => fetchPortfolios(shopId),
    enabled: Boolean(shop && !shopError),
    staleTime: 60_000,
  });
  const normalizedName = portfolioName.trim().toLowerCase();
  const nameConflict =
    normalizedName.length > 0 &&
    (portfolios.data ?? []).some(
      (portfolio) => portfolio.name.trim().toLowerCase() === normalizedName,
    );

  const create = useCreateTemplatePortfolioDraft();
  const update = useUpdateTemplatePortfolioDraft(shopId ?? "", draft?.id ?? "");
  const saving = create.isPending || update.isPending;

  const submit = form.handleSubmit(async (values) => {
    if (!shopId || shopError || !values.product || nameConflict) return;
    const namePrefix = values.namePrefix.trim();
    // Another product gets a fresh structure: its own category target and no drawer edits.
    const kept =
      draft?.settings.product?.asin === values.product.asin ? draft?.settings : undefined;
    // Drawer names embed the prefix, so a new prefix drops them and keeps the other edits.
    const campaignOverrides =
      kept?.campaignOverrides && draft?.settings.namePrefix !== namePrefix
        ? Object.fromEntries(
            Object.entries(kept.campaignOverrides).map(([key, { name: _name, ...rest }]) => [
              key,
              rest,
            ]),
          )
        : kept?.campaignOverrides;
    const settings: TemplatePortfolioEditableSettings = {
      categoryTarget: kept?.categoryTarget ?? {
        categoryId: null,
        categoryName: null,
        priceGreaterThan: 80,
        priceLessThan: 150,
      },
      excludedCampaignKeys: kept?.excludedCampaignKeys,
      campaignOverrides,
      product: { asin: values.product.asin, listingKey: values.product.listingKey },
      namePrefix,
      portfolioName: values.portfolioName.trim(),
      // ponytail: start date = the day ① is saved; the API moves a past date to today on execute.
      startDate: draft?.settings.startDate ?? format(new Date(), "yyyy-MM-dd"),
    };
    try {
      const saved = draft
        ? await update.mutateAsync({ expectedVersion: draft.version, settings })
        : await create.mutateAsync({ shopId, settings });
      onSaved(saved);
    } catch (error) {
      toast.error(await getApiErrorMessage(error));
    }
  });

  function requestShopChange(nextShopId: string) {
    if (nextShopId === shopId) return;
    if (draft && draft.keywords.length > 0) setPendingShopId(nextShopId);
    else changeShop(nextShopId);
  }
  function changeShop(nextShopId: string) {
    form.setValue("product", null);
    syncPortfolioName({ product: null });
    onShopChange(nextShopId);
  }

  const productError = form.formState.errors.product?.message;
  const prefixError = form.formState.errors.namePrefix?.message;
  const nameError =
    form.formState.errors.portfolioName?.message ??
    (nameConflict ? t("basics.nameConflict") : null);
  const canContinue = Boolean(shop && !shopError && product);

  return (
    <form
      className="flex flex-1 flex-col"
      onSubmit={(event) => {
        event.preventDefault();
        void submit();
      }}
    >
      <div className="flex flex-col gap-6 pb-6">
        <Field
          invalid={Boolean(shopError)}
          className={cn("gap-1.5", !shopError && shop && "border-b border-line pb-5")}
        >
          <FieldLabel className="text-sm font-semibold text-ink sm:text-sm">
            {`${t("basics.shop")} *`}
          </FieldLabel>
          {accounts.isLoading ? (
            <Skeleton className="h-9 w-[360px]" />
          ) : (
            <Select
              items={shops.map((candidate) => ({
                value: candidate.shopId,
                label: candidate.shopName,
              }))}
              value={shop ? shop.shopId : null}
              onValueChange={(value) => value && requestShopChange(value)}
            >
              <SelectTrigger
                className="h-9 w-[360px] gap-2 px-2.5"
                aria-invalid={Boolean(shopError)}
                aria-label={t("basics.shop")}
              >
                {shop ? (
                  <span className="flex min-w-0 items-center gap-2">
                    <IconBuildingStore className="size-4 text-ink" aria-hidden />
                    <span className="truncate text-[13px] font-medium text-ink">
                      {shop.shopName}
                    </span>
                    <span className="shrink-0 text-xs text-muted-foreground">
                      {marketplaceCountry(shop.marketplaceName, i18n.language)}
                    </span>
                  </span>
                ) : (
                  <span className="text-[13px] text-muted-foreground">
                    {t("basics.shopPlaceholder")}
                  </span>
                )}
              </SelectTrigger>
              <SelectContent className="w-[360px]">
                <SelectGroup>
                  {shops.map((candidate) => {
                    const usd = isUsdShop(candidate);
                    return (
                      <SelectItem
                        key={candidate.shopId}
                        value={candidate.shopId}
                        disabled={!usd}
                        className="h-9 data-disabled:opacity-100"
                      >
                        <span
                          className={cn("flex min-w-0 items-center gap-2", !usd && "opacity-50")}
                        >
                          <span className="truncate text-[13px] font-medium text-ink">
                            {candidate.shopName}
                          </span>
                          <span className="shrink-0 text-xs text-muted-foreground">
                            {marketplaceCountry(candidate.marketplaceName, i18n.language)}
                          </span>
                        </span>
                        {usd ? null : (
                          <span className="ml-auto shrink-0 text-[11px] text-muted-foreground">
                            {t("basics.shopUnsupportedItem")}
                          </span>
                        )}
                      </SelectItem>
                    );
                  })}
                </SelectGroup>
              </SelectContent>
            </Select>
          )}
          {shopError ? <BasicsFieldError>{shopError}</BasicsFieldError> : null}
        </Field>

        {shop && !shopError ? (
          <>
            <Field invalid={Boolean(productError)} className="gap-3">
              <FieldLabel className="text-sm font-semibold text-ink sm:text-sm">
                {`${t("basics.product")} *`}
              </FieldLabel>
              <div className="w-full">
                <Controller
                  control={form.control}
                  name="product"
                  render={({ field }) => (
                    <ProductListingPicker
                      key={shop.shopId}
                      shopId={shop.shopId}
                      marketplaceId={shop.marketplaceId}
                      selectionMode="single"
                      selectionLimit={1}
                      // Template portfolios never gated on stock; execution ignores it too.
                      inventory="ignore"
                      selected={selectedProducts}
                      onSelectedChange={(products) => {
                        const next = products[0] ?? null;
                        field.onChange(next);
                        syncPortfolioName({ product: next });
                      }}
                      enterInput=""
                      onEnterInputChange={noop}
                      invalidProducts={NO_INVALID_PRODUCTS}
                      onInvalidProductsChange={noop}
                      onCatalogBlockingChange={noop}
                      onReconnect={() =>
                        void navigate({ to: "/settings", search: { tab: "amazon" } })
                      }
                      initialAvailability={null}
                      allowCatalogRefresh={false}
                    />
                  )}
                />
              </div>
              {productError ? <BasicsFieldError>{productError}</BasicsFieldError> : null}
            </Field>

            <div className="flex flex-col gap-4 border-t border-line pt-5">
              <Field invalid={Boolean(prefixError)} className="gap-1.5">
                <FieldLabel htmlFor="template-portfolio-prefix" className="text-xs sm:text-xs">
                  {`${t("basics.namePrefix")} *`}
                </FieldLabel>
                <Input
                  id="template-portfolio-prefix"
                  className="text-[13px] [&_input]:px-2.5"
                  placeholder={t("basics.namePrefixPlaceholder")}
                  aria-invalid={Boolean(prefixError)}
                  {...form.register("namePrefix", {
                    onChange: (event) => {
                      prefixEdited.current = true;
                      syncPortfolioName({ namePrefix: event.target.value });
                    },
                  })}
                />
                {prefixError ? <BasicsFieldError>{prefixError}</BasicsFieldError> : null}
              </Field>
              <Field invalid={Boolean(nameError)} className="gap-1.5">
                <div className="flex w-full items-center justify-between">
                  <FieldLabel htmlFor="template-portfolio-name" className="text-xs sm:text-xs">
                    {`${t("basics.portfolioName")} *`}
                  </FieldLabel>
                  {product && namePrefix.trim() ? (
                    <button
                      type="button"
                      className="text-xs text-primary outline-none hover:underline focus-visible:ring-2 focus-visible:ring-ring"
                      onClick={() => {
                        nameEdited.current = false;
                        form.setValue("portfolioName", defaultName, { shouldValidate: true });
                      }}
                    >
                      {t("basics.restoreDefault")}
                    </button>
                  ) : null}
                </div>
                <div className="relative w-full">
                  <Input
                    id="template-portfolio-name"
                    className="text-[13px] [&_input]:pr-20 [&_input]:pl-2.5"
                    placeholder={t("basics.portfolioNamePlaceholder")}
                    aria-invalid={Boolean(nameError)}
                    {...form.register("portfolioName", {
                      onChange: () => {
                        nameEdited.current = true;
                      },
                    })}
                  />
                  <span className="pointer-events-none absolute top-1/2 right-2.5 -translate-y-1/2 text-xs text-muted-foreground tabular-nums">
                    {portfolioName.length} / {NAME_MAX}
                  </span>
                </div>
                {nameError ? <BasicsFieldError>{nameError}</BasicsFieldError> : null}
              </Field>
            </div>

            <div className="flex items-center gap-2 rounded-md border border-line bg-canvas px-3.5 py-2.5">
              <IconPlayerPause className="size-4 shrink-0 text-text-secondary" aria-hidden />
              <p className="text-xs text-text-secondary">{t("basics.pausedHint")}</p>
            </div>
          </>
        ) : null}
      </div>

      <TemplatePortfolioFooter
        hint={
          <Button
            type="button"
            variant="outline"
            disabled={saving}
            onClick={() => void navigate({ to: "/campaigns" })}
          >
            {t("actions.cancel")}
          </Button>
        }
      >
        <Button type="submit" loading={saving} disabled={!canContinue}>
          {t("actions.nextKeywords")}
          <IconArrowRight data-icon="inline-end" className="size-3.5" />
        </Button>
      </TemplatePortfolioFooter>

      <AlertDialog
        open={pendingShopId !== null}
        onOpenChange={(open) => !open && setPendingShopId(null)}
      >
        <AlertDialogPopup>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("switchShop.title")}</AlertDialogTitle>
            <AlertDialogDescription>{t("switchShop.description")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogClose render={<Button variant="outline" />}>
              {t("switchShop.cancel")}
            </AlertDialogClose>
            <AlertDialogClose
              render={
                <Button
                  variant="destructive"
                  onClick={() => pendingShopId && changeShop(pendingShopId)}
                />
              }
            >
              {t("switchShop.confirm")}
            </AlertDialogClose>
          </AlertDialogFooter>
        </AlertDialogPopup>
      </AlertDialog>
    </form>
  );
}

function BasicsFieldError({ children }: { children: React.ReactNode }) {
  return (
    <FieldError match className="flex items-center gap-1.5">
      <IconAlertCircle className="size-3.5 shrink-0" stroke={1.8} aria-hidden />
      {children}
    </FieldError>
  );
}
